import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import useDashboardsService from 'src/services/dashboardService'
import useMenusStore from 'src/stores/useMenusStore'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { AppMenuSavedDashboardInterface } from 'src/types/AppMenuInterface'
import { findDashboardsOnMenusFn, setSubProductDashboardsOnMenusFn } from 'src/utils/menuUtils'

const useToggleFavoriteDashboard = ({
  subProductCode,
  program,
  dashboard,
}: {
  subProductCode: string
  program: string | undefined
  dashboard: Pick<AppMenuSavedDashboardInterface, 'id' | 'name' | 'favorite' | 'program_id'> | null
}) => {
  const { selectedDashboard, setSelectedDashboard } = useSelectedDashboardStore()
  const { setAsFavoriteDashboard } = useDashboardsService()
  const { setMenus, menus } = useMenusStore()
  const [loading, setLoading] = useState(false)

  const loadingRef = useRef(loading)

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  const handleSetDefaultDashboard = useCallback(() => {
    if (!dashboard?.id || loadingRef.current) return
    setLoading(true)
    const { id } = dashboard
    const handleSetDefaultDashboard = async () => {
      const isFavorite = dashboard.favorite
      return toast
        .promise(
          setAsFavoriteDashboard({
            id: isFavorite ? null : dashboard.id,
            code: subProductCode,
            program,
          }),
          {
            loading: 'Please wait...',
            success: (
              <span>
                <b>{dashboard.name}</b> {!isFavorite ? 'set' : 'unset'} as favorite
              </span>
            ),
            error: 'An error occurred. Please retry.',
          },
          { duration: 4000 },
        )
        .then(data => {
          const dashboards = data?.data || []

          const dashboardAdded = dashboards.find(d => d.id === id)

          if (!dashboardAdded) {
            return
          }

          const isDashboardFavorite = dashboardAdded.favorite

          const oldDashboards = findDashboardsOnMenusFn({
            menus,
            subProductCode,
            programId: program?.toString(),
          })

          const dashboardMenus: AppMenuSavedDashboardInterface[] = oldDashboards.map(d => {
            if (d.id === dashboard.id) {
              return {
                ...d,
                favorite: isDashboardFavorite,
              }
            }
            return {
              ...d,
              favorite: false,
            }
          })

          setMenus(s => ({
            ...s,
            menus: setSubProductDashboardsOnMenusFn({
              menus: s.menus,
              dashboards: dashboardMenus,
              subProductCode,
              programId: program?.toString(),
            }),
          }))

          if (selectedDashboard && selectedDashboard?.id === dashboard.id) {
            setSelectedDashboard({ ...selectedDashboard, favorite: isDashboardFavorite })
          }
        })
        .catch(() => {
          setLoading(false)
        })
    }
    handleSetDefaultDashboard().finally(() => setLoading(false))
  }, [selectedDashboard, subProductCode, program, setMenus, setSelectedDashboard, dashboard])

  return { handleSetDefaultDashboard, loading, isFavorite: Boolean(dashboard?.favorite) }
}

export default useToggleFavoriteDashboard
